import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, CheckCircle2, Home as HomeIcon, Bell, ChevronRight, Activity, Calendar, X } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { atHomeApi, consultationApi, carePlanApi } from "@/lib/api";
import { toast } from "sonner";

const VITAL_METRICS = [
  { key: "bp", label: "Blood Pressure", fields: [{ k: "systolic", ph: "Systolic" }, { k: "diastolic", ph: "Diastolic" }] },
  { key: "glucose", label: "Blood Sugar", unit: "mg/dL", fields: [{ k: "value", ph: "mg/dL" }] },
  { key: "spo2", label: "SpO₂", unit: "%", fields: [{ k: "value", ph: "%" }] },
  { key: "weight", label: "Weight", unit: "kg", fields: [{ k: "value", ph: "kg" }] },
];

const VITAL_META = {
  bp: { label: "BP", unit: "mmHg", tint: "#DCFCE7" },
  glucose: { label: "Sugar", unit: "mg/dL", tint: "#DBEAFE" },
  spo2: { label: "SpO₂", unit: "%", tint: "#EDE9FE" },
  weight: { label: "Weight", unit: "kg", tint: "#FEF3C7" },
  heart_rate: { label: "Pulse", unit: "bpm", tint: "#FCE7F3" },
};

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3.6e6);
    if (h < 1) return "Just now";
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return ""; }
}

function fmtVital(metric, value) {
  if (value && typeof value === "object") {
    if (metric === "bp" && value.systolic != null) return `${value.systolic}/${value.diastolic ?? ""}`;
    if (value.value != null) return String(value.value);
    const first = Object.values(value).find((x) => x != null);
    return first != null ? String(first) : "";
  }
  return String(value ?? "");
}

function latestVitals(entries) {
  const seen = {};
  for (const e of entries) {
    const m = (e.metric || "").toLowerCase();
    if (VITAL_META[m] && !seen[m]) seen[m] = e;
  }
  return Object.keys(VITAL_META)
    .filter((m) => seen[m])
    .slice(0, 3)
    .map((m) => ({ ...VITAL_META[m], value: fmtVital(m, seen[m].value), when: timeAgo(seen[m].measured_at) }));
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function SeniorCareDashboard() {
  const navigate = useNavigate();
  const name = useAuthStore((s) => s.displayName());
  const user = useAuthStore((s) => s.user);
  const [nextVisit, setNextVisit] = useState(null);
  const [nextAppt, setNextAppt] = useState(null);
  const [vitals, setVitals] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [logMetric, setLogMetric] = useState("bp");
  const [logForm, setLogForm] = useState({});
  const [saving, setSaving] = useState(false);

  const uid = user?._id;
  const today = new Date().toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  const firstName = (name || "there").split(" ")[0];

  const load = useCallback(async () => {
    if (!uid) return;
    const [ahc, cons, vit] = await Promise.allSettled([
      atHomeApi.bookings(uid),
      consultationApi.listPatient(uid),
      carePlanApi.vitals(uid, 90),
    ]);

    let ahcList = [];
    if (ahc.status === "fulfilled") {
      ahcList = (ahc.value.data?.bookings || []).filter((b) => b.status === "pending" || b.status === "confirmed");
      setNextVisit(ahcList[0] || null);
    }

    let consList = [];
    if (cons.status === "fulfilled") {
      const raw = cons.value.data;
      consList = Array.isArray(raw) ? raw : (raw?.consultations || raw?.bookings || []);
      setNextAppt(consList.find((c) => ["CONFIRMED", "confirmed", "PENDING_DOCTOR", "PENDING_HOSPITAL"].includes(c.status)) || consList[0] || null);
    }

    if (vit.status === "fulfilled") {
      setVitals(latestVitals(vit.value.data?.entries || []));
    }

    const items = [];
    for (const b of ahcList.slice(0, 3)) {
      const when = b.booking_date ? new Date(b.booking_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "";
      items.push({
        when: [when, b.time_slot].filter(Boolean).join(" · "),
        title: b.service_name || b.service_type || "Home visit",
        sub: b.provider_name || b.service_data?.reason || "Home care",
        done: b.status === "completed",
      });
    }
    for (const c of consList.filter((c) => ["CONFIRMED", "confirmed"].includes(c.status)).slice(0, 2)) {
      items.push({
        when: [c.preferred_date || c.appointment_date, c.preferred_time || c.appointment_time].filter(Boolean).join(" · "),
        title: c.doctor_name || c.doctorName || "Doctor appointment",
        sub: c.hospital_name || c.specialty || "Consultation",
        done: false,
      });
    }
    setTimeline(items);
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  const activeMetric = VITAL_METRICS.find((m) => m.key === logMetric);
  const openLog = () => { setLogMetric("bp"); setLogForm({}); setLogOpen(true); };

  const saveVital = async () => {
    if (!uid || !activeMetric) return;
    const nums = activeMetric.fields.map((f) => logForm[f.k]);
    if (nums.some((n) => n == null || n === "" || isNaN(Number(n)))) {
      toast.error("Please enter a valid number");
      return;
    }
    const value = logMetric === "bp"
      ? { systolic: Number(logForm.systolic), diastolic: Number(logForm.diastolic) }
      : { value: Number(logForm.value), ...(activeMetric.unit ? { unit: activeMetric.unit } : {}) };
    try {
      setSaving(true);
      await carePlanApi.logVital({ user_id: uid, metric: logMetric, value });
      toast.success("Vital logged");
      setLogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Set up your profile first.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} data-testid="dashboard-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button onClick={() => navigate("/senior-care/services")} data-testid="dashboard-book-care" className="flex items-center gap-1.5 rounded-full px-4 py-2 text-white text-sm font-bold" style={{ backgroundColor: "#2563EB" }}>
          <Plus className="h-4 w-4" /> Book Care
        </button>
      </div>

      {/* Greeting */}
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-full flex items-center justify-center text-2xl font-extrabold" style={{ backgroundColor: "#DBEAFE", color: "#2563EB" }}>{firstName.charAt(0).toUpperCase()}</div>
        <div>
          <p className="text-sm text-muted-foreground">{greeting()},</p>
          <p className="text-2xl font-extrabold" style={{ color: "#1E3A8A" }}>{name || "Welcome"} 👋</p>
          <p className="text-xs text-muted-foreground">{today}</p>
        </div>
      </div>

      <div className="flex justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold" style={{ backgroundColor: "#DCFCE7", color: "#15803D" }}>
          <CheckCircle2 className="h-4 w-4" /> Doing well
        </span>
      </div>

      {/* Vitals */}
      <div className="flex items-center justify-between -mb-1">
        <h2 className="text-base font-extrabold" style={{ color: "#1E3A8A" }}>Vitals</h2>
        <button onClick={openLog} data-testid="log-vitals-btn" className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold" style={{ backgroundColor: "#DBEAFE", color: "#2563EB" }}>
          <Plus className="h-3.5 w-3.5" /> Log
        </button>
      </div>
      {vitals.length > 0 ? (
        <div className="grid grid-cols-3 gap-3">
          {vitals.map((v) => (
            <div key={v.label} className="rounded-2xl p-4 text-center" style={{ backgroundColor: v.tint }}>
              <p className="text-xs font-bold text-slate-600">{v.label}</p>
              <p className="text-2xl font-extrabold text-slate-900 mt-1">{v.value}</p>
              {v.unit ? <p className="text-[11px] text-slate-500">{v.unit}</p> : null}
              <span className="inline-block rounded-lg px-2.5 py-0.5 text-[11px] font-bold mt-2 bg-white/70" style={{ color: "#15803D" }}>{v.when || "Logged"}</span>
            </div>
          ))}
        </div>
      ) : (
        <button onClick={openLog} data-testid="vitals-empty" className="w-full flex items-center gap-3 rounded-2xl p-4 text-left" style={{ backgroundColor: "#EFF6FF", border: "1px solid #DBEAFE" }}>
          <Activity className="h-5 w-5 shrink-0" style={{ color: "#2563EB" }} />
          <div className="flex-1">
            <p className="font-bold" style={{ color: "#1E3A8A" }}>No vitals logged yet</p>
            <p className="text-xs text-muted-foreground">Tap to log BP, sugar or SpO₂</p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </button>
      )}

      {/* Next home visit */}
      <button onClick={() => navigate("/at-home-care/my-bookings")} data-testid="next-home-visit" className="w-full flex items-center gap-4 rounded-2xl p-4 text-left" style={{ backgroundColor: "#EFF6FF", border: "1px solid #DBEAFE" }}>
        <div className="h-13 w-13 rounded-full flex items-center justify-center p-3.5" style={{ backgroundColor: "#DBEAFE" }}><HomeIcon className="h-6 w-6" style={{ color: "#2563EB" }} /></div>
        <div className="flex-1">
          <p className="text-xs text-muted-foreground">Next home visit</p>
          {nextVisit ? (
            <>
              <p className="text-lg font-extrabold" style={{ color: "#1E3A8A" }}>{nextVisit.service_name || nextVisit.service_type || "Home Visit"}</p>
              <p className="text-sm text-slate-600">{[nextVisit.booking_date ? new Date(nextVisit.booking_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : null, nextVisit.time_slot].filter(Boolean).join(" · ") || "Scheduled"}</p>
              {nextVisit.provider_name ? <p className="text-sm text-slate-600">{nextVisit.provider_name}</p> : null}
              <span className="inline-block rounded-lg px-2 py-0.5 text-[10px] font-bold mt-1" style={{ backgroundColor: "#DBEAFE", color: "#2563EB" }}>{(nextVisit.status || "pending").toUpperCase()}</span>
            </>
          ) : (
            <>
              <p className="text-lg font-extrabold" style={{ color: "#1E3A8A" }}>No upcoming visit</p>
              <p className="text-sm text-slate-600">Book a nurse or home-care visit</p>
            </>
          )}
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      </button>

      {/* Med + Appt */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => navigate("/medications")} data-testid="medication-card" className="rounded-2xl border border-border bg-card p-4 text-left hover:shadow-md transition-all">
          <p className="font-bold text-sm">💊  Medication</p>
          <p className="text-xs text-muted-foreground mt-1">Manage your medicines</p>
          <p className="text-xs font-bold mt-2" style={{ color: "#2563EB" }}>View meds ›</p>
        </button>
        <button onClick={() => navigate("/consultations")} data-testid="next-appointment-card" className="rounded-2xl border border-border bg-card p-4 text-left hover:shadow-md transition-all">
          <p className="font-bold text-sm">📅  Next appointment</p>
          {nextAppt ? (
            <>
              <p className="text-sm font-extrabold mt-1" style={{ color: "#1E3A8A" }}>{nextAppt.doctor_name || nextAppt.doctorName || "Doctor appointment"}</p>
              <p className="text-xs text-muted-foreground">{nextAppt.hospital_name || nextAppt.specialty || "Consultation"}</p>
            </>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">No upcoming appointment</p>
          )}
        </button>
      </div>

      {/* SOS */}
      <button onClick={() => navigate("/emergency")} data-testid="sos-emergency" className="w-full rounded-2xl p-5 text-center" style={{ backgroundColor: "#FECACA" }}>
        <div className="flex items-center justify-center gap-2"><Bell className="h-6 w-6" style={{ color: "#DC2626" }} /><span className="text-2xl font-extrabold" style={{ color: "#DC2626" }}>SOS</span></div>
        <p className="text-base font-extrabold mt-1" style={{ color: "#991B1B" }}>Emergency Help</p>
        <p className="text-xs" style={{ color: "#B91C1C" }}>Tap for immediate assistance</p>
      </button>

      {/* Upcoming care */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-extrabold" style={{ color: "#1E3A8A" }}>Upcoming care</h2>
        <button onClick={() => navigate("/at-home-care/my-bookings")} className="rounded-xl border border-border px-3 py-1.5 text-xs font-bold" style={{ color: "#2563EB" }}>View all</button>
      </div>
      {timeline.length > 0 ? (
        <div className="space-y-0">
          {timeline.map((t, i) => (
            <div key={i} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="h-3.5 w-3.5 rounded-full mt-1" style={{ backgroundColor: "#2563EB" }} />
                {i < timeline.length - 1 && <div className="w-0.5 flex-1" style={{ backgroundColor: "#BFDBFE" }} />}
              </div>
              <div className="pb-5">
                <p className="text-xs font-bold text-muted-foreground">{t.when || "Scheduled"}</p>
                <p className="font-bold flex items-center gap-1.5">{t.title}{t.done && <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "#16A34A" }} />}</p>
                <p className="text-xs text-muted-foreground">{t.sub}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-6 text-center">
          <Calendar className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground font-semibold">No scheduled care right now</p>
          <button onClick={() => navigate("/senior-care/services")} className="rounded-xl px-5 py-2 text-white text-sm font-bold" style={{ backgroundColor: "#2563EB" }}>Book care</button>
        </div>
      )}

      {/* Log vitals modal */}
      {logOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => setLogOpen(false)} data-testid="log-vitals-modal">
          <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-card p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-extrabold" style={{ color: "#1E3A8A" }}>Log a vital</h3>
              <button onClick={() => setLogOpen(false)} data-testid="log-vitals-close" className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              {VITAL_METRICS.map((m) => (
                <button key={m.key} onClick={() => { setLogMetric(m.key); setLogForm({}); }} data-testid={`metric-${m.key}`}
                  className="rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors"
                  style={logMetric === m.key ? { backgroundColor: "#2563EB", color: "#fff", borderColor: "#2563EB" } : { backgroundColor: "transparent", color: "#475569", borderColor: "#E2E8F0" }}>
                  {m.label}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              {activeMetric.fields.map((f) => (
                <input key={f.k} type="number" inputMode="numeric" placeholder={f.ph} data-testid={`vital-input-${f.k}`}
                  value={logForm[f.k] ?? ""} onChange={(e) => setLogForm((s) => ({ ...s, [f.k]: e.target.value }))}
                  className="flex-1 rounded-xl border border-border bg-background px-4 py-3 text-base outline-none focus:border-blue-400" />
              ))}
            </div>
            <button onClick={saveVital} disabled={saving} data-testid="log-vitals-save"
              className="w-full rounded-xl py-3 text-white font-bold disabled:opacity-60" style={{ backgroundColor: "#2563EB" }}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
